import { CDK_DRAG_PARENT, CdkDragHandle } from '@angular/cdk/drag-drop';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Optional,
  Output,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';

import { BoardsAbstract } from '@main-application/boards/abstract/board-abstract.component';
import { EColorPalette } from '@shared/enums/color-palette.enum';
import { EIcon } from '@shared/enums/icon.enum';
import { TemplateEditorItemComponent } from '@template/components/template-editor-dialog/template-editor-area/template-editor-item/template-editor-item.component';
import { TemplateEditorBaseDnd } from '@template/components/template-editor-dialog/template-editor-base/template-editor-base-dnd.component';
import {
  AreaModel,
  AreaPageModel,
  TemplateEditorArea,
  TemplateEditorRatingGroup,
} from '@template/models/template-editor-model';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';

@UntilDestroy()
@Component({
  selector: 'app-template-editor-area',
  templateUrl: './template-editor-area.component.html',
  styleUrls: ['./template-editor-area.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemplateEditorAreaComponent extends TemplateEditorBaseDnd implements AfterViewInit {
  EIcon = EIcon;
  EColorPalette = EColorPalette;
  editMode = false;

  @Output() remove = new EventEmitter<AreaModel>();
  @Output() areaChange = new EventEmitter<AreaModel>();
  @Output() enterPressed = new EventEmitter<AreaModel>();

  areaContent: TemplateEditorArea;
  _area: AreaModel;
  @Input() set area(area: AreaModel) {
    this._area = area;
    this.areaContent = new TemplateEditorArea(area);
  }
  get area(): AreaModel {
    return this._area;
  }

  @ViewChild('areaName', { read: ElementRef, static: false }) areaName: ElementRef<HTMLDivElement>;
  @ViewChild(CdkDragHandle, { static: true }) handle: CdkDragHandle;

  @ViewChildren(TemplateEditorItemComponent) areaItems!: QueryList<TemplateEditorItemComponent>;
  constructor(
    protected store: Store<{}>,
    protected cdr: ChangeDetectorRef,
    snackbarService: SnackbarService,
    renderer: Renderer2,
    @Optional() @Inject(CDK_DRAG_PARENT) public cdk
  ) {
    super(store, snackbarService, renderer, cdr);
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();

    this.cdk._handles.length = 1;
    this.cdk._handles._results = [this.handle];
    this.cdk._handles.changes.next();
  }

  add() {
    const itemMaxNumber = this.areaContent.items
      .filter(e => /^Item [0-9]*$/.test(e.title))
      .map(c => +c.title.replace('Item ', ''))
      .reduce((prev, max) => (prev > max ? prev : max), 0);

    const name = this.areaContent.addItem(`Item ${itemMaxNumber + 1}`);
    // TODO: change to observable on change
    this.emitItemsChange();
    this.cdr.markForCheck();
    setTimeout(() => {
      const item = this.areaItems.find(c => c.name === name);
      item?.enterEditMode();
    }, 100);
  }

  emitItemsChange() {
    this.areaChange.emit({ ...this.area, areaSurvey: { ...this.area.areaSurvey, pages: this.areaContent.items } });
  }

  dropItem(data: AreaPageModel[]) {
    super.drop(data);
    this.emitItemsChange();
  }
  get id() {
    return this.area.id;
  }
  enterEditMode() {
    if (!this.checkEditRight() || this.editMode) {
      return;
    }
    this.editMode = true;
    this.cdr.detectChanges();
    const div = this.areaName.nativeElement;
    setTimeout(() => {
      const range = document.createRange();
      range.selectNodeContents(div);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }, 0);
  }

  nameChanged($event: KeyboardEvent) {
    if ($event.key === 'Enter') {
      this.areaName.nativeElement.blur();
      this.enterPressed.emit(this.area);
      $event.stopPropagation();
      $event.preventDefault();
    }
  }

  leaveEditMode(value: string) {
    value = value.trim();
    this.editMode = false;
    if (this.area.title !== value) {
      this.areaChange.emit({ ...this.area, title: value, areaSurvey: { ...this.area.areaSurvey, title: value } });
    }

    setTimeout(() => {
      const div = this.areaName.nativeElement;
      div.focus();
      div.blur();
    }, 1);
  }

  removeClick() {
    this.remove.emit(this.area);
  }

  removeItem(item: AreaPageModel) {
    if (!this.checkEditRight()) {
      return;
    }
    this.areaContent.removeItem(item.name);
    this.emitItemsChange();
  }

  itemChange(item: AreaPageModel) {
    if (!this.checkEditRight()) {
      return;
    }
    this.areaContent.updateItem(item);
    this.emitItemsChange();
  }

  addRatingGroup([item, group]: [item: AreaPageModel, group: TemplateEditorRatingGroup]) {
    if (!this.checkEditRight()) {
      return;
    }
    this.areaContent.addRatingGroup(item, group);
    this.emitItemsChange();
  }

  removeRatingGroup(item: AreaPageModel) {
    this.areaContent.removeRatingGroup(item);
  }

  enterPressedHandler(item: AreaPageModel) {
    const index = this.areaContent.items.findIndex(itemInner => itemInner.name === item.name);
    if (index === this.areaContent.items.length - 1) {
      this.add();
    } else {
      const name = this.areaContent.items[index + 1].name;
      const selectedItem = this.areaItems.find(c => c.name === name);
      selectedItem?.enterEditMode();
    }
  }
}
